import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { clsx } from "clsx";
import { GripVertical, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";

import type {
	AttachRdoPhotoInput,
	RdoActivityInput,
	StageStatus,
} from "@/adapters/api";
import { TimePicker } from "@/components/time-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { StageCombobox } from "@/features/stages/components/stage-combobox";
import type { Stage } from "@/features/stages/types.ts";
import { STAGE_STATUS, STAGE_STATUSES } from "@/features/stages/utils/status.ts";
import type { RdoActivity } from "../../types.ts";
import { combineReportDateTime, formatRdoTime } from "../../utils/format.ts";
import {
	formatPhotoCount,
	isPhotoLimitReached,
	MAX_RDO_PHOTOS,
} from "../../utils/photos.ts";
import { PhotoPickerDialog } from "../photo-picker-dialog";

const NO_STATUS = "none";

interface RdoActivityCardProps {
	activity: RdoActivity;
	index: number;
	constructionId: string;
	reportDate: string;
	stages: Stage[];
	readOnly: boolean;
	onUpdate: (input: RdoActivityInput) => void;
	onDelete: () => void;
	onAttachPhotos: (inputs: AttachRdoPhotoInput[]) => Promise<unknown>;
	onUpdateCaption: (photoId: string, caption: string) => void;
	onDetachPhoto: (photoId: string) => void;
}

export function RdoActivityCard({
	activity,
	index,
	constructionId,
	reportDate,
	stages,
	readOnly,
	onUpdate,
	onDelete,
	onAttachPhotos,
	onUpdateCaption,
	onDetachPhoto,
}: RdoActivityCardProps) {
	const [title, setTitle] = useState(activity.title);
	const [description, setDescription] = useState(activity.description);
	const [time, setTime] = useState(formatRdoTime(activity.occurredAt));
	const [pickerOpen, setPickerOpen] = useState(false);
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id: activity.id, disabled: readOnly });

	const stageName = stages.find((stage) => stage.id === activity.stageId)?.name;
	const attachedMediaIds = new Set(
		activity.photos.flatMap((photo) =>
			photo.sourcePostMediaId ? [photo.sourcePostMediaId] : [],
		),
	);

	function commit(overrides: Partial<RdoActivityInput> = {}) {
		const nextTitle = title.trim() || activity.title;
		if (!title.trim()) setTitle(activity.title);
		const occurredAt =
			combineReportDateTime(reportDate, time) ?? activity.occurredAt;
		const input: RdoActivityInput = {
			title: nextTitle,
			stageId: activity.stageId,
			status: activity.status,
			occurredAt,
			description,
			...overrides,
		};
		const unchanged =
			input.title === activity.title &&
			(input.stageId ?? "") === (activity.stageId ?? "") &&
			(input.status ?? "") === (activity.status ?? "") &&
			input.occurredAt === activity.occurredAt &&
			input.description === activity.description;
		if (!unchanged) onUpdate(input);
	}

	const titleId = `rdo-activity-${activity.id}-title`;
	const timeId = `rdo-activity-${activity.id}-time`;
	const stageId = `rdo-activity-${activity.id}-stage`;
	const statusId = `rdo-activity-${activity.id}-status`;
	const descriptionId = `rdo-activity-${activity.id}-description`;

	return (
		<article
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={clsx("rdo-activity", isDragging && "is-dragging")}
			data-testid="rdo-activity"
			aria-label={`Atividade ${index + 1}: ${activity.title}`}
		>
			<header className="rdo-activity__header">
				{!readOnly && (
					<button
						type="button"
						className="rdo-activity__handle"
						aria-label={`Reordenar atividade ${index + 1}`}
						{...attributes}
						{...listeners}
					>
						<GripVertical />
					</button>
				)}
				<span className="rdo-activity__index">{index + 1}</span>
				{readOnly ? (
					<h3 className="rdo-activity__title">{activity.title}</h3>
				) : (
					<Input
						id={titleId}
						aria-label="Título da atividade"
						className="rdo-activity__title-input"
						value={title}
						maxLength={200}
						onChange={(event) => setTitle(event.target.value)}
						onBlur={() => commit()}
					/>
				)}
				{!readOnly && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="rdo-activity__delete"
						aria-label={`Remover atividade ${index + 1}`}
						onClick={onDelete}
					>
						<Trash2 />
					</Button>
				)}
			</header>

			<div className="rdo-activity__fields">
				<div className="rdo-activity__field">
					<Label htmlFor={stageId}>Etapa</Label>
					{readOnly ? (
						<p className="rdo-activity__value">{stageName ?? "Sem etapa"}</p>
					) : (
						<StageCombobox
							id={stageId}
							stages={stages}
							value={activity.stageId ?? ""}
							onChange={(next) => commit({ stageId: next || undefined })}
							placeholder="Sem etapa"
							noneLabel="Sem etapa"
						/>
					)}
				</div>
				<div className="rdo-activity__field">
					<Label htmlFor={statusId}>Status</Label>
					{readOnly ? (
						<p className="rdo-activity__value">
							{activity.status ? STAGE_STATUS[activity.status].label : "—"}
						</p>
					) : (
						<Select
							value={activity.status ?? NO_STATUS}
							onValueChange={(next) =>
								commit({
									status: next === NO_STATUS ? undefined : (next as StageStatus),
								})
							}
						>
							<SelectTrigger id={statusId} className="rdo-activity__select">
								<SelectValue placeholder="Sem status" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={NO_STATUS}>Sem status</SelectItem>
								{STAGE_STATUSES.map((status) => (
									<SelectItem key={status} value={status}>
										{STAGE_STATUS[status].label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				</div>
				<div className="rdo-activity__field rdo-activity__field--time">
					<Label htmlFor={timeId}>Horário</Label>
					{readOnly ? (
						<p className="rdo-activity__value">
							{formatRdoTime(activity.occurredAt)}
						</p>
					) : (
						<TimePicker
							id={timeId}
							value={time}
							onChange={(next) => {
								setTime(next);
								commit({
									occurredAt:
										combineReportDateTime(reportDate, next) ??
										activity.occurredAt,
								});
							}}
						/>
					)}
				</div>
			</div>

			<div className="rdo-activity__field">
				<Label htmlFor={descriptionId}>Descrição</Label>
				{readOnly ? (
					<p className="rdo-activity__description">
						{activity.description || "—"}
					</p>
				) : (
					<Textarea
						id={descriptionId}
						className="rdo-activity__textarea"
						value={description}
						maxLength={4000}
						onChange={(event) => setDescription(event.target.value)}
						onBlur={() => commit()}
					/>
				)}
			</div>

			<div className="rdo-activity__photos">
				<div className="rdo-activity__photos-header">
					<span className="rdo-activity__photos-title">Fotos</span>
					<span className="rdo-activity__photos-count">
						{formatPhotoCount(activity.photos.length)}
					</span>
				</div>
				<div className="rdo-photo-grid">
					{activity.photos.map((photo) => (
						<figure key={photo.id} className="rdo-photo" data-testid="rdo-photo">
							<img src={photo.url} alt={photo.caption || "Foto da atividade"} />
							{!readOnly && (
								<button
									type="button"
									className="rdo-photo__remove"
									aria-label="Remover foto"
									onClick={() => onDetachPhoto(photo.id)}
								>
									<X />
								</button>
							)}
							{readOnly ? (
								photo.caption && (
									<figcaption className="rdo-photo__caption">
										{photo.caption}
									</figcaption>
								)
							) : (
								<PhotoCaptionInput
									photoId={photo.id}
									caption={photo.caption}
									onCommit={onUpdateCaption}
								/>
							)}
						</figure>
					))}
					{!readOnly && (
						<button
							type="button"
							className="rdo-photo-grid__add"
							aria-label="Adicionar foto"
							disabled={isPhotoLimitReached(activity.photos.length)}
							onClick={() => setPickerOpen(true)}
						>
							<Plus />
							<span>{`Máx. ${MAX_RDO_PHOTOS}`}</span>
						</button>
					)}
				</div>
			</div>

			{!readOnly && (
				<PhotoPickerDialog
					open={pickerOpen}
					onOpenChange={setPickerOpen}
					constructionId={constructionId}
					reportDate={reportDate}
					currentCount={activity.photos.length}
					attachedMediaIds={attachedMediaIds}
					onAttach={onAttachPhotos}
				/>
			)}
		</article>
	);
}

function PhotoCaptionInput({
	photoId,
	caption,
	onCommit,
}: {
	photoId: string;
	caption: string;
	onCommit: (photoId: string, caption: string) => void;
}) {
	const [value, setValue] = useState(caption);
	return (
		<Input
			aria-label="Legenda da foto"
			className="rdo-photo__caption-input"
			placeholder="Legenda"
			value={value}
			maxLength={200}
			onChange={(event) => setValue(event.target.value)}
			onBlur={() => {
				if (value.trim() !== caption) onCommit(photoId, value.trim());
			}}
		/>
	);
}
